"use client";
import React, { useEffect, useState, useRef, useCallback } from "react";

import LoginForm from "@/components/Form/User/login-form";
import SignUpForm from "@/components/Form/User/sign-up-form";
import ResetPasswordForm from "@/components/Form/User/reset-password-form";

import { Link } from "@/navigation";
import LoginModal from "./login-modal";
import { useTranslations } from "next-intl";
import { useUserStore } from "@/store/user.store";
import { deleteCookie, setCookie } from "cookies-next";
import { useRouter } from "@/navigation";
import { useUserAuth } from "@/lib/hooks/useUserAuth";
import { message } from "antd";
import { LogoutOutlined, UserSwitchOutlined, UserAddOutlined } from "@ant-design/icons";

type Props = {
	children: React.ReactNode;
};

export type SignSwitchProps = {
	activeId: number;
	setActiveId: (id: number) => void;
	setOpenModal: (open: boolean) => void;
};

export function SignSwitch({ activeId = 1, setActiveId, setOpenModal }: SignSwitchProps) {
	const t = useTranslations();
	const [isLoading, setIsLoading] = useState(false);
	const list = [
		{
			id: 1,
			label: "common.Sign_in",
			description: "message.e6d7f9862ff4ff40a9eaf8523c08e6c4624c",
			component: <LoginForm setLoginModalOn={setOpenModal} />,
		},
		{
			id: 2,
			label: "common.Sign_Up",
			description: "message.b0e9e6e07105254449b889ccac3a508d8c88",
			component: <SignUpForm setActive={setActiveId} />,
		},
		{
			id: 3,
			label: "common.Reset_password",
			description: "message.e6d7f9862ff4ff40a9eaf8523c08e6c4624c",
			component: <ResetPasswordForm setLoginModalOn={setOpenModal} />,
		},
	];
	const googleLoginUrl = `${process.env.NEXT_PUBLIC_LANGUAGE_LIST_URL}/user/google_login`;
	const handleGoogleLogin = async () => {
		try {
			setIsLoading(true);
			setCookie("googleLoginRedirectPath", window.location.href);

			const res = await fetch(googleLoginUrl);
			const data = (await res.json()) as any;
			// console.log(data, "data");

			window.location.href = data.detail.approval_url;
		} catch (error) {
			console.error("Google login error:", error);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="w-full max-w-lg overflow-hidden bg-white rounded-md">
			{activeId === 1 && (
				<>
					{/* 标题栏 */}
					<div className="relative bg-mainColor px-8 py-6 rounded-tl-md rounded-tr-md">
						<h3 className="pr-10 text-2xl font-bold text-white sm:text-3xl">
							{t("common.Log_in")}
						</h3>
						<button
							onClick={() => setOpenModal(false)}
							className="absolute top-6 right-6 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white transition-all duration-200 hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50"
							aria-label="Close"
						>
							<i className="ri-close-line text-2xl"></i>
						</button>
					</div>
					
					{/* 表单内容 */}
					<div className="px-8 py-8 sm:px-10 sm:py-10 max-md:px-4">
						{/* Logo */}
						<div className="flex justify-center mb-6">
							<img 
								src="/image/plsyy/logo.png" 
								alt="Logo" 
								className="h-12 sm:h-16 object-contain"
							/>
						</div>
						
						<div>{list[activeId - 1].component}</div>

						<div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-6">
							<span className="cursor-pointer text-sm text-mainColor hover:text-mainColor/80 transition-colors duration-200" onClick={() => setActiveId(3)}>
								{t("common.New_customer")}?
							</span>
							<button
								onClick={() => setActiveId(2)}
								className="inline-flex items-center text-sm font-medium text-mainColor transition-all duration-200 hover:text-mainColor/80"
							>
								{t("common.Create_your_account")}{" "}
								<i className="ri-arrow-right-line ml-1.5 max-md:hidden"></i>
							</button>
						</div>
					</div>
				</>
			)}
			{activeId === 2 && (
				<>
					{/* 标题栏 */}
					<div className="relative bg-mainColor px-8 py-6 rounded-tl-md rounded-tr-md">
						<h3 className="pr-10 text-2xl font-bold text-white sm:text-3xl">
							{t("common.Create_your_account")}
						</h3>
						<button
							onClick={() => setOpenModal(false)}
							className="absolute top-6 right-6 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white transition-all duration-200 hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50"
							aria-label="Close"
						>
							<i className="ri-close-line text-2xl"></i>
						</button>
					</div>
					
					{/* 表单内容 */}
					<div className="px-8 py-8 sm:px-10 sm:py-10 max-md:px-4">
						{/* Logo */}
						<div className="flex justify-center mb-6">
							<img 
								src="/image/plsyy/logo.png" 
								alt="Logo" 
								className="h-12 sm:h-16 object-contain"
							/>
						</div>
						
						<div>{list[activeId - 1].component}</div>

						<div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-6">
							<span className="cursor-pointer text-sm text-mainColor hover:text-mainColor/80 transition-colors duration-200" onClick={() => setActiveId(3)}>
								{t("common.New_customer")}?
							</span>
							<button
								onClick={() => setActiveId(1)}
								className="inline-flex items-center text-sm font-medium text-mainColor transition-all duration-200 hover:text-mainColor/80"
							>
								{t("common.Sign_in")}{" "}
								<i className="ri-arrow-right-line ml-1.5 max-md:hidden"></i>
							</button>
						</div>
					</div>
				</>
			)}
			{activeId === 3 && (
				<>
					{/* 标题栏 */}
					<div className="relative bg-mainColor px-8 py-6 rounded-tl-md rounded-tr-md">
						<h3 className="pr-10 text-2xl font-bold text-white sm:text-3xl">
							{t("common.Reset_password")}
						</h3>
						<button
							onClick={() => setOpenModal(false)}
							className="absolute top-6 right-6 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white transition-all duration-200 hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50"
							aria-label="Close"
						>
							<i className="ri-close-line text-2xl"></i>
						</button>
					</div>
					
					{/* 表单内容 */}
					<div className="px-8 py-8 sm:px-10 sm:py-10 max-md:px-4">
						{/* Logo */}
						<div className="flex justify-center mb-6">
							<img 
								src="/image/plsyy/logo.png" 
								alt="Logo" 
								className="h-12 sm:h-16 object-contain"
							/>
						</div>
						
						<div>{list[activeId - 1].component}</div>

						<div className="mt-6 flex items-center justify-end border-t border-gray-200 pt-6">
							<button
								onClick={() => setActiveId(1)}
								className="inline-flex items-center text-sm font-medium text-mainColor transition-all duration-200 hover:text-mainColor/80"
							>
								{t("common.Return_to_Login")}
								<i className="ri-arrow-right-line ml-1.5 max-md:hidden"></i>
							</button>
						</div>
					</div>
				</>
			)}
		</div>
	);
}

type NavUserList = {
	id: number;
	label: string;
	show: boolean;
	callback?: (item: NavUserList) => void;
	link?: string;
	icon?: JSX.Element;
};

export default function NavUser({ children }: Props) {
	const [openModal, setOpenModal] = useState(false);
	const [activeId, setActiveId] = useState(1);
	const [list, setList] = useState<NavUserList[]>([]);
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const { userInfo, getUserInfo } = useUserStore();
	const t = useTranslations();
	const router = useRouter();
	const { useSignOut } = useUserAuth();
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);

	// 鼠标进入事件，立即显示下拉菜单
	const handleMouseEnter = useCallback(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
		setIsMenuOpen(true);
	}, []);

	// 鼠标离开事件，添加延迟关闭
	const handleMouseLeave = useCallback(() => {
		timeoutRef.current = setTimeout(() => {
			setIsMenuOpen(false);
			timeoutRef.current = null;
		}, 200); // 200ms的延迟，给用户足够时间移动鼠标
	}, []);

	useEffect(() => {
		setList([
			{
				id: 1,
				label: "common.login",
				show: !userInfo,
				callback: (option: NavUserList) => {
					setOpenModal(true);
					setActiveId(option.id);
					setIsMenuOpen(false);
				},
				icon: <UserSwitchOutlined />,
			},
			{
				id: 2,
				label: "common.register",
				show: !userInfo,
				callback: (option: NavUserList) => {
					setOpenModal(true);
					setActiveId(option.id);
					setIsMenuOpen(false);
				},
				icon: <UserAddOutlined />,
			},
			{
				id: 3,
				label: "common.center",
				show: !!userInfo,
				link: "/center",
				icon: <UserSwitchOutlined />,
			},
			{
				id: 4,
				label: "common.logout",
				show: !!userInfo,
				callback: () => {
					deleteCookie("__user__login__info");
					deleteCookie("user-store");
					useSignOut();
					router.push("/");
					message.success(t("message.logout successful"));
					setIsMenuOpen(false);
				},
				icon: <LogoutOutlined />,
			},
		]);
	}, [userInfo]);

	useEffect(() => {
		getUserInfo();
	}, []);

	// 清理timeout，避免内存泄漏
	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	return (
		<div 
			className="relative inline-block group"
			ref={dropdownRef}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
		>
			{children}
			{isMenuOpen && (
				<div
					className="absolute top-full !left-0 z-50 w-36 bg-white shadow-lg border border-mainColor/20"
					style={{ 
						minWidth: "max-content",
						marginTop: 0,
						paddingTop: "2px",
						paddingBottom: "2px",
					}}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
				>
				<div className="">
					{list.map(
						(item) =>
							item.show && (
								<div
									key={item.id}
									id={item.id === 1 ? "web-login" : ""}
									className="transition-colors duration-150"
									onClick={() => item.callback && item.callback(item)}
								>
									{item.link ? (
										<Link
											href={item.link}
											className="flex w-full items-center gap-2 whitespace-nowrap px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-mainColor/10 hover:text-mainColor"
										>
											<span className="text-mainColor">{item.icon}</span>
											<span>{t(item.label)}</span>
										</Link>
									) : (
										<button className="flex w-full items-center gap-2 whitespace-nowrap px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-mainColor/10 hover:text-mainColor">
											<span className="text-mainColor">{item.icon}</span>
											<span>{t(item.label)}</span>
										</button>
									)}
								</div>
							),
					)}
				</div>
				</div>
			)}
			<LoginModal
				openModal={openModal}
				setOpenModal={setOpenModal}
				activeId={activeId}
				setActiveId={setActiveId}
			/>
		</div>
	);
}

const GoogleSVG = () => {
	return (
		<svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
			<path
				fill="#EA4335"
				d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
			/>
			<path
				fill="#4285F4"
				d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
			/>
			<path
				fill="#FBBC05"
				d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
			/>
			<path
				fill="#34A853"
				d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
			/>
		</svg>
	);
};
